import { useState, useEffect } from "react";
import axios from "axios";
import {
  Calendar, Clock, User, Loader2, Plus, RefreshCw,
  CheckCircle2, XCircle, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function RequestServiceMedicalCalendar() {
  const auth = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [slots, setSlots] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("my"); // my | all | slots
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [visitNotes, setVisitNotes] = useState("");

  const [newSlot, setNewSlot] = useState({
    doctor_id: "",
    day_of_week: "Monday",
    start_time: "09:00",
    end_time: "17:00",
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [aptRes, slotRes, docRes] = await Promise.all([
        axios.get(`${API}/request/appointments`, { headers }),
        axios.get(`${API}/request/appointments/slots`, { headers }),
        axios.get(`${API}/request/doctors`, { headers }),
      ]);
      setAppointments(aptRes.data || []);
      setSlots(slotRes.data || []);
      setDoctors(docRes.data || []);
    } catch (err) {
      toast.error("Failed to load medical data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleBookAppointment = async () => {
    if (!selectedSlot) return;
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/request/appointments`,
        { slot_id: selectedSlot.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appointment booked!");
      setBookingOpen(false);
      setSelectedSlot(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to book appointment");
    }
  };

  const handleCancelAppointment = async (aptId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/request/appointments/${aptId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appointment cancelled");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const handleCompleteAppointment = async (aptId) => {
    if (!visitNotes) {
      toast.error("Please add visit notes");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/request/appointments/${aptId}/complete`,
        { visit_notes: visitNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Appointment completed");
      setVisitNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete");
    }
  };

  const handleNoShow = async (aptId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/request/appointments/${aptId}/no-show`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Marked as no-show");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleCreateSlot = async () => {
    if (!newSlot.doctor_id) {
      toast.error("Select a doctor");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/request/appointments/slots`, newSlot, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Slot created");
      setNewSlot({ doctor_id: "", day_of_week: "Monday", start_time: "09:00", end_time: "17:00" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create slot");
    }
  };

  const myAppointments = appointments.filter(
    (a) => a.patient_id === auth.user?.user_id || a.patient_name === auth.user?.name
  );

  const displayAppointments = view === "my" ? myAppointments : appointments;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          Medical Calendar
        </h1>
        <p className="text-slate-500 mt-1">Book and manage medical appointments</p>
      </div>

      {/* View Tabs */}
      <div className="flex gap-2 mb-6">
        {["my", "all", "slots"].map((v) => (
          <Button
            key={v}
            variant={view === v ? "default" : "outline"}
            onClick={() => setView(v)}
            className={view === v ? "bg-teal-500 hover:bg-teal-600" : ""}
          >
            {v === "my" ? "My Appointments" : v === "all" ? "All Appointments" : "Manage Slots"}
          </Button>
        ))}
        <Button variant="outline" onClick={fetchData} className="ml-auto">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* My / All Appointments View */}
      {view !== "slots" && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-slate-800">
              {view === "my" ? "My Appointments" : "All Appointments"}
            </h2>
            <Button onClick={() => setBookingOpen(true)} className="bg-teal-500 hover:bg-teal-600">
              <Plus className="w-4 h-4 mr-2" /> Book Appointment
            </Button>
          </div>
          {displayAppointments.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-slate-200">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No appointments found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-medium text-slate-800">
                          {apt.doctor_name || apt.doctor_id}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            apt.status === "booked"
                              ? "bg-blue-100 text-blue-700"
                              : apt.status === "completed"
                              ? "bg-emerald-100 text-emerald-700"
                              : apt.status === "cancelled"
                              ? "bg-red-100 text-red-700"
                              : "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {apt.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {apt.date ? new Date(apt.date).toLocaleDateString() : "-"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {apt.start_time} - {apt.end_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {apt.patient_name || apt.patient_id}
                        </span>
                      </div>
                      {apt.visit_notes && (
                        <p className="text-sm text-slate-600 mt-2 bg-app p-2 rounded">
                          {apt.visit_notes}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {apt.status === "booked" && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCompleteAppointment(apt.id)}
                            className="text-emerald-600"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Complete
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleNoShow(apt.id)}
                            className="text-amber-600"
                          >
                            <XCircle className="w-4 h-4 mr-1" /> No Show
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCancelAppointment(apt.id)}
                            className="text-red-600"
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Manage Slots View */}
      {view === "slots" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Create Slot Form */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4">Create Slot</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Doctor</label>
                <Select
                  value={newSlot.doctor_id}
                  onValueChange={(v) => setNewSlot({ ...newSlot, doctor_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id}>
                        {doc.name} — {doc.specialization}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Day of Week</label>
                <Select
                  value={newSlot.day_of_week}
                  onValueChange={(v) => setNewSlot({ ...newSlot, day_of_week: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_NAMES.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start</label>
                  <Input
                    type="time"
                    value={newSlot.start_time}
                    onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End</label>
                  <Input
                    type="time"
                    value={newSlot.end_time}
                    onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                  />
                </div>
              </div>
              <Button onClick={handleCreateSlot} className="bg-teal-500 hover:bg-teal-600 w-full">
                <Plus className="w-4 h-4 mr-2" /> Create Slot
              </Button>
            </div>
          </div>

          {/* Slots List */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4">Existing Slots</h3>
            {slots.length === 0 ? (
              <p className="text-slate-500 text-sm">No slots configured.</p>
            ) : (
              <div className="space-y-2">
                {slots.map((slot) => (
                  <div
                    key={slot.id}
                    className="flex items-center justify-between p-3 bg-app rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-slate-700">
                        {slot.doctor_name || slot.doctor_id}
                      </span>
                      <span className="text-sm text-slate-500">{slot.day_of_week}</span>
                      <span className="text-sm text-slate-500">
                        {slot.start_time} - {slot.end_time}
                      </span>
                      {slot.is_blocked && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">
                          Blocked
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book Appointment</DialogTitle>
            <DialogDescription>Select a doctor and available slot</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {doctors.length === 0 ? (
              <p className="text-slate-500">No doctors available.</p>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Available Slots</label>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {slots
                    .filter((s) => !s.is_blocked)
                    .map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => setSelectedSlot(slot)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          selectedSlot?.id === slot.id
                            ? "border-teal-500 bg-teal-50"
                            : "border-slate-200 hover:border-teal-300"
                        }`}
                      >
                        <p className="font-medium text-slate-800 text-sm">
                          {slot.doctor_name || slot.doctor_id}
                        </p>
                        <p className="text-xs text-slate-500">
                          {slot.day_of_week} · {slot.start_time} - {slot.end_time}
                        </p>
                      </button>
                    ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setBookingOpen(false);
                  setSelectedSlot(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBookAppointment}
                disabled={!selectedSlot}
                className="bg-teal-500 hover:bg-teal-600"
              >
                Confirm Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
